import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminEvent } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  UploadCloud, Database, FileSpreadsheet, CheckCircle2, AlertTriangle, Plane, BedDouble,
  ArrowRight, X, Loader2, ChevronRight, ChevronDown, MapPin,
} from "lucide-react";

const BADGE = {
  new: "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]",
  matched: "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]",
  updated: "bg-[hsl(var(--secondary)/0.15)] text-[hsl(var(--secondary))]",
  unchanged: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]",
  cancelled: "bg-[hsl(0_60%_92%)] text-[hsl(0_55%_40%)]",
  review: "bg-[hsl(38_80%_90%)] text-[hsl(38_50%_35%)]",
  unmatched: "bg-[hsl(0_70%_92%)] text-[hsl(0_60%_42%)]",
  skipped: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]",
  error: "bg-[hsl(0_70%_92%)] text-[hsl(0_60%_42%)]",
};

const PREVIEW_FIELDS = [
  ["email", "Email"],
  ["attendee_group", "Group / Category"],
  ["rsvp_status", "RSVP / Status"],
  ["designated_arrival_date", "Designated Arrival"],
  ["designated_departure_date", "Designated Departure"],
  ["planned_hotel_arrival", "Hotel Arrival"],
  ["planned_hotel_departure", "Hotel Departure"],
  ["company_paid_nights", "Company-Paid Nights"],
  ["employee_paid_nights", "Extension Nights"],
  ["guest_exists", "Has Plus-One"],
  ["guest_first_name", "Guest First Name"],
  ["guest_last_name", "Guest Last Name"],
  ["guest_relationship", "Guest Relationship"],
  ["employee_gala_meal", "Employee Gala Meal"],
  ["guest_gala_meal", "Guest Gala Meal"],
];

function fmtVal(v) {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

function rsvpLabel(v) {
  const s = String(v ?? "").trim().toLowerCase();
  if (["y", "yes", "attending", "accepted", "confirmed", "attended"].includes(s)) return "Attending";
  if (["n", "no", "declined", "cancelled", "canceled"].includes(s)) return "Declined";
  if (["", "pending", "rsvp_pending"].includes(s)) return "Pending";
  return fmtVal(v);
}

function AccommodationPreview({ accom }) {
  const [open, setOpen] = useState(false);
  if (!accom) return null;
  const t = accom.status;
  const statusLabel = { calculated: "Calculated", pending: "Pending — stay dates not confirmed",
    not_applicable: "Not applicable (declined / cancelled)" }[t] || t;
  return (
    <div className="mt-3 rounded-lg border border-[hsl(var(--pc-gold)/0.35)] bg-[hsl(var(--pc-gold)/0.06)] p-3" data-testid="review-accommodation">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Accommodation (system calculated)</span>
        <span className="text-[10px] font-semibold rounded-full bg-[hsl(var(--pc-gold)/0.2)] text-[hsl(38_50%_32%)] px-2 py-0.5">{statusLabel}</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-xs">
        <Row k="Covered nights" v={accom.covered_nights ?? 0} />
        <Row k="Personal extension nights" v={accom.extension_nights ?? 0} />
        <Row k="Total nights" v={accom.total_nights ?? 0} />
        <Row k="Company Pay" v={accom.company_display ?? "$0.00"} />
        <Row k="Employee Pay" v={accom.employee_display ?? "$0.00"} />
        <Row k="Payment method" v="Direct to Hotel" />
      </div>
      {accom.status === "pending" && accom.reason && <div className="mt-1.5 text-[11px] italic text-[hsl(var(--muted-foreground))]">{accom.reason}</div>}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-2 border-b border-[hsl(var(--border)/0.6)] py-0.5">
      <span className="text-[hsl(var(--muted-foreground))]">{k}</span>
      <span className="font-medium text-[hsl(var(--primary))] text-right">{fmtVal(v)}</span>
    </div>
  );
}

function CanonicalPreview({ rec, segments, accom }) {
  if (!rec) return null;
  return (
    <div className="rounded-lg bg-[hsl(var(--muted)/0.4)] p-3" data-testid="attendee-preview">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
        {PREVIEW_FIELDS.map(([k, label]) => (
          <div key={k} className="flex justify-between gap-2 text-xs border-b border-[hsl(var(--border)/0.6)] py-0.5">
            <span className="text-[hsl(var(--muted-foreground))]">{label}</span>
            <span className="font-medium text-[hsl(var(--primary))] text-right truncate"
              data-testid={k === "attendee_group" ? "preview-category" : k === "rsvp_status" ? "preview-rsvp" : undefined}
              title={k === "rsvp_status" ? rsvpLabel(rec[k]) : fmtVal(rec[k])}>
              {k === "rsvp_status" ? rsvpLabel(rec[k]) : fmtVal(rec[k])}
            </span>
          </div>
        ))}
      </div>
      <AccommodationPreview accom={accom} />
      <div className="mt-3">
        <div className="text-[10px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Flight Itinerary</div>
        {(!segments || segments.length === 0) ? (
          <div className="text-xs text-[hsl(var(--muted-foreground))] italic">No flight segments parsed</div>
        ) : (
          <div className="space-y-1">
            {segments.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-xs" data-testid="flight-segment">
                <span className="rounded-full bg-[hsl(var(--secondary)/0.15)] text-[hsl(var(--secondary))] px-2 py-0.5 font-semibold capitalize">{s.direction}</span>
                <span className="font-medium text-[hsl(var(--primary))]">{[s.airline, s.flight_number].filter(Boolean).join(" ") || "—"}</span>
                <span className="text-[hsl(var(--muted-foreground))]">{[s.date, s.time].filter(Boolean).join(" ")}</span>
                {(s.from || s.to) && <span className="text-[hsl(var(--muted-foreground))]">{s.from && s.to ? `${s.from} → ${s.to}` : s.from ? `from ${s.from}` : `to ${s.to}`}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Denom({ label, m }) {
  const num = m?.num ?? 0, den = m?.den ?? 0;
  return (
    <div className="rounded-xl border border-[hsl(var(--border))] p-3 text-center">
      <div className="font-serif text-2xl text-[hsl(var(--primary))]">{num} <span className="text-base text-[hsl(var(--muted-foreground))]">/ {den}</span></div>
      <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] mt-0.5">{label}</div>
    </div>
  );
}


function Bar({ label, value }) {
  return (
    <div data-testid={`readiness-${label.toLowerCase()}`}>
      <div className="flex justify-between text-xs mb-1"><span className="text-[hsl(var(--muted-foreground))]">{label}</span><span className="font-semibold text-[hsl(var(--primary))]">{value}%</span></div>
      <div className="h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden"><div className="h-full bg-[hsl(var(--secondary))]" style={{ width: `${value}%` }} /></div>
    </div>
  );
}

function TransferConfig({ eventId }) {
  const [cfg, setCfg] = useState(null);
  const [gateways, setGateways] = useState("");
  const [tol, setTol] = useState(120);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    api.dataGetTransferConfig(eventId).then((c) => {
      setCfg(c);
      setGateways((c.gateway_airports || []).join(", "));
      setTol(c.tolerance_minutes ?? 120);
    }).catch(() => {});
  }, [eventId]);

  async function save() {
    setSaving(true);
    try {
      const list = gateways.split(",").map((s) => s.trim()).filter(Boolean);
      const res = await api.dataPutTransferConfig(eventId, { gateway_airports: list, transfer_tolerance_minutes: Number(tol) || 120 });
      setCfg(res);
      toast.success("Transfer settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save settings");
    } finally { setSaving(false); }
  }

  if (!cfg) return null;
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-white p-5" data-testid="transfer-config">
      <h2 className="font-semibold text-[hsl(var(--primary))] mb-1 flex items-center gap-2"><MapPin className="h-4 w-4 text-[hsl(var(--secondary))]" /> Transfer Settings</h2>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">Used when matching Amstar transfers. Collabera covers airport transfers only on designated event dates.</p>
      <div className="grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-[hsl(var(--muted-foreground))] mb-1">Event gateway airport codes (comma-separated)</label>
          <input value={gateways} onChange={(e) => setGateways(e.target.value)} placeholder="e.g. SJD, PUJ" data-testid="config-gateways"
            className="w-full rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--secondary))]" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[hsl(var(--muted-foreground))] mb-1">Departure buffer (min)</label>
          <input type="number" min="0" value={tol} onChange={(e) => setTol(e.target.value)} data-testid="config-tolerance"
            className="w-28 rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--secondary))]" />
        </div>
        <button onClick={save} disabled={saving} data-testid="config-save"
          className="rounded-xl bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold hover:brightness-110 transition-[filter] disabled:opacity-60">
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

export default function DataImports() {
  const { eventId } = useAdminEvent();
  const navigate = useNavigate();
  const [sources, setSources] = useState(null);
  const [reviewExc, setReviewExc] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [history, setHistory] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [wizard, setWizard] = useState(null); // {step, job, sheet, mapping, sheetInfo, analysis}
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    api.dataSources(eventId).then(setSources).catch(() => {});
    api.dataReadiness(eventId).then(setReadiness).catch(() => {});
    api.dataHistory(eventId).then(setHistory).catch(() => {});
    api.dataExceptions(eventId).then(setExceptions).catch(() => {});
  }, [eventId]);

  useEffect(() => { refresh(); }, [refresh]);

  async function onFile(e, sourceType) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.dataUpload(eventId, sourceType, fd);
      setWizard({ step: "sheet", job: res, sheet: res.sheets[0]?.name, mapping: {}, sheetInfo: null, analysis: null, sourceType });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  }

  async function loadSheet(sheet) {
    setBusy(true);
    try {
      const info = await api.dataSheet(eventId, wizard.job.job_id, sheet);
      setWizard((w) => ({ ...w, step: "map", sheet, sheetInfo: info, mapping: { ...info.suggested_mapping } }));
    } catch (err) { toast.error("Could not read worksheet"); } finally { setBusy(false); }
  }

  async function analyze() {
    setBusy(true);
    try {
      const res = await api.dataAnalyze(eventId, wizard.job.job_id, { sheet: wizard.sheet, mapping: wizard.mapping });
      setWizard((w) => ({ ...w, step: "review", analysis: res }));
    } catch (err) { toast.error(err?.response?.data?.detail || "Analysis failed"); } finally { setBusy(false); }
  }

  async function commit() {
    setBusy(true);
    try {
      const res = await api.dataCommit(eventId, wizard.job.job_id, {});
      setWizard((w) => ({ ...w, step: "done", commit: res.counts }));
      refresh();
      toast.success("Import committed");
    } catch (err) { toast.error(err?.response?.data?.detail || "Commit failed"); } finally { setBusy(false); }
  }

  const SourceCard = ({ icon: Icon, title, type, enabled }) => {
    const s = sources?.[type] || {};
    return (
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-white p-5" data-testid={`source-card-${type}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Icon className="h-5 w-5 text-[hsl(var(--secondary))]" /><h3 className="font-semibold text-[hsl(var(--primary))]">{title}</h3></div>
          <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 capitalize ${enabled ? (s.status === "active" ? "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]") : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>{enabled ? (s.status || "not started").replace("_", " ") : "Not Activated"}</span>
        </div>
        <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))] space-y-1">
          <div>Last import: {s.last_import ? new Date(s.last_import).toLocaleString() : "—"}</div>
          <div>Version: {s.version || 0}</div>
          {enabled && s.records && ((type === "HYATT" || type === "AMSTAR")
            ? <div>Committed: {s.records.matched || 0} matched · {s.records.review || 0} review · {s.records.unmatched || 0} {type === "AMSTAR" ? "unmatched" : "orphan"}</div>
            : <div>Committed: {s.records.created || 0} new · {s.records.updated || 0} updated · {s.records.cancelled || 0} cancelled</div>)}
        </div>
        {enabled ? (
          <label className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold cursor-pointer hover:brightness-110 transition-[filter]" data-testid={`upload-${type}`}>
            <UploadCloud className="h-4 w-4" /> Upload {title.split(" ")[0]}
            <input type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => onFile(e, type)} />
          </label>
        ) : (
          <div className="mt-4 text-xs italic text-[hsl(var(--muted-foreground))]">Awaiting final file</div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="data-imports-page">
      <div>
        <div className="flex items-center gap-2 text-[hsl(var(--secondary))]"><Database className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wide">Data & Imports</span></div>
        <h1 className="font-serif text-2xl text-[hsl(var(--primary))]">Canonical Import Center</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">The single place to bring authoritative data into this event. Hotel/Rooming and Transfers activate once their final files are received.</p>
      </div>

      {/* Canonical roster metrics */}
      {readiness && (
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-white p-5" data-testid="readiness-panel">
          <h2 className="font-semibold text-[hsl(var(--primary))] mb-4">Roster & Readiness</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {readiness.canonical && [
              ["Roster", readiness.canonical.roster],
              ["Attending", readiness.canonical.attending],
              ["Pending", readiness.canonical.pending],
              ["Declined", readiness.canonical.declined],
            ].map(([k, v]) => (
              <div key={k} className="rounded-xl bg-[hsl(var(--muted)/0.5)] p-3 text-center" data-testid={`metric-${k.toLowerCase()}`}>
                <div className="font-serif text-3xl text-[hsl(var(--primary))]">{v}</div>
                <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] mt-0.5">{k}</div>
              </div>
            ))}
          </div>
          {typeof readiness.canonical?.participating === "number" && (
            <div className="mb-4 text-xs text-[hsl(var(--muted-foreground))]" data-testid="metric-participating">
              Participating (active, excludes Declined):{" "}
              <span className="font-semibold text-[hsl(var(--primary))]">{readiness.canonical.participating}</span>
            </div>
          )}
          {readiness.canonical && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Denom label="Meal Selection" m={readiness.canonical.meal_selection} />
              <Denom label="Guest Details" m={readiness.canonical.guest_details} />
              <div className="rounded-xl border border-[hsl(var(--border))] p-3 text-center">
                <div className="font-serif text-2xl text-[hsl(var(--primary))]">{readiness.canonical.plus_ones}</div>
                <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] mt-0.5">Plus-Ones</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Source cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <button onClick={() => navigate("/manager/pc-master-data")} data-testid="source-card-PC_MASTER_DATA" className="rounded-2xl border border-[hsl(var(--border))] bg-white p-5 text-left hover:bg-[hsl(var(--muted)/0.4)] transition-colors">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-[hsl(var(--secondary))]" /><h3 className="font-semibold text-[hsl(var(--primary))]">PC Master Data</h3></div>
            <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-[hsl(var(--mint))] text-[hsl(var(--primary))]">Active</span>
          </div>
          <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">Canonical attendee roster: profile, RSVP, classification, plus-one, stay dates and T-shirt source. Upload the APP_UPLOAD workbook (Upload → Analyze → Review → Apply).</div>
          <div className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold hover:brightness-110 transition-[filter]" data-testid="open-pc-master-data">Open PC Master Data <ArrowRight className="h-4 w-4" /></div>
        </button>
        <button onClick={() => navigate("/manager/flight-reconciliation")} data-testid="source-card-FLIGHT_RECON" className="rounded-2xl border border-[hsl(var(--border))] bg-white p-5 text-left hover:bg-[hsl(var(--muted)/0.4)] transition-colors">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Plane className="h-5 w-5 text-[hsl(var(--secondary))]" /><h3 className="font-semibold text-[hsl(var(--primary))]">Flight Reconciliation</h3></div>
            <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-[hsl(var(--mint))] text-[hsl(var(--primary))]">Active</span>
          </div>
          <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">The single ingestion &amp; reconciliation center for flights — Travel Master and Navan manifests are analyzed, dry-run and applied against the canonical baseline.</div>
          <div className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold hover:brightness-110 transition-[filter]" data-testid="open-flight-reconciliation">Open Flight Reconciliation <ArrowRight className="h-4 w-4" /></div>
        </button>
        <SourceCard icon={BedDouble} title="Hotel / Rooming" type="HYATT" />
        <SourceCard icon={Plane} title="Transfers" type="AMSTAR" />
      </div>

      {/* Transfer settings (event gateway + departure pickup buffer) */}
      <TransferConfig eventId={eventId} />

      {/* Exceptions + History */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-white p-5" data-testid="exceptions-panel">
          <h2 className="font-semibold text-[hsl(var(--primary))] mb-3 flex items-center gap-2" data-testid="exceptions-heading"><AlertTriangle className="h-4 w-4 text-[hsl(38_60%_45%)]" /> Exceptions / Review Queue <span className="text-sm font-normal text-[hsl(var(--muted-foreground))]">— Current Open: <span className="font-semibold text-[hsl(var(--primary))]" data-testid="exceptions-current-open">{exceptions.filter((e) => e.bucket === "current").length}</span> · Historical: <span className="font-semibold" data-testid="exceptions-historical">{exceptions.filter((e) => e.bucket !== "current").length}</span></span></h2>
          <div className="space-y-2 max-h-72 overflow-auto">
            {exceptions.length === 0 && <p className="text-sm text-[hsl(var(--muted-foreground))]">No exceptions.</p>}
            {exceptions.length > 0 && exceptions.filter((e) => e.bucket === "current").length === 0 && <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="exceptions-current-empty">No current operational exceptions — all active-module records are within the authoritative roster.</p>}
            {exceptions.filter((e) => e.bucket === "current").length > 0 && <div className="text-[10px] uppercase font-bold tracking-wide text-[hsl(38_50%_40%)]">Current — active modules</div>}
            {exceptions.filter((e) => e.bucket === "current").map((x) => (
              <button key={x.id} onClick={() => setReviewExc(x)} className="w-full flex items-center justify-between gap-2 rounded-lg border border-[hsl(var(--border))] p-2.5 text-sm text-left hover:bg-[hsl(var(--muted)/0.4)]" data-testid="exception-item">
                <div className="min-w-0"><div className="font-medium text-[hsl(var(--primary))] truncate">{x.attendee_name || "Unmatched record"}<span className="ml-1 text-xs font-normal text-[hsl(var(--muted-foreground))]">· {x.source}</span></div><div className="text-xs text-[hsl(var(--muted-foreground))]">{x.description}</div></div>
                <div className="flex items-center gap-1 shrink-0">
                  <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${x.severity === "high" ? "bg-[hsl(0_70%_92%)] text-[hsl(0_60%_42%)]" : x.severity === "warning" ? "bg-[hsl(38_80%_90%)] text-[hsl(38_50%_35%)]" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>{x.status}</span>
                  {x.resolvable && <span className="text-xs text-[hsl(var(--secondary))] font-semibold" data-testid="exception-review">Review</span>}
                </div>
              </button>
            ))}
            {exceptions.filter((e) => e.bucket !== "current").length > 0 && <div className="text-[10px] uppercase font-bold tracking-wide text-[hsl(var(--muted-foreground))] pt-2">Historical / Test — inactive modules (audit preserved)</div>}
            {exceptions.filter((e) => e.bucket !== "current").map((x) => (
              <div key={x.id} className="flex items-center justify-between gap-2 rounded-lg border border-[hsl(var(--border)/0.6)] bg-[hsl(var(--muted)/0.3)] p-2.5 text-sm" data-testid="exception-item-historical">
                <div className="min-w-0"><div className="font-medium text-[hsl(var(--primary))] truncate">{x.attendee_name || "Unmatched record"}<span className="ml-1 text-xs font-normal text-[hsl(var(--muted-foreground))]">· {x.source}</span></div><div className="text-xs text-[hsl(var(--muted-foreground))]">{x.description}</div></div>
                <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] shrink-0">read-only</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-white p-5" data-testid="history-panel">
          <h2 className="font-semibold text-[hsl(var(--primary))] mb-3">Import History</h2>
          <div className="space-y-2 max-h-72 overflow-auto">
            {history.length === 0 && <p className="text-sm text-[hsl(var(--muted-foreground))]">No imports yet.</p>}
            {history.map((j) => (
              <div key={j.id} className="rounded-lg border border-[hsl(var(--border))] p-2.5 text-sm" data-testid="history-item">
                <div className="flex justify-between"><span className="font-medium text-[hsl(var(--primary))]">{j.source_type.replace("_", " ")} · v{j.version}</span><span className="text-xs capitalize text-[hsl(var(--muted-foreground))]">{j.status.replace("_", " ")}</span></div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{j.file_name} · {j.committed_at ? new Date(j.committed_at).toLocaleString() : "not committed"}</div>
                {j.commit_counts && <div className="text-xs text-[hsl(var(--muted-foreground))]">{j.commit_counts.created} new · {j.commit_counts.updated} updated · {j.commit_counts.cancelled} cancelled · {j.commit_counts.exceptions} exceptions</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {wizard && (
        <Wizard wizard={wizard} setWizard={setWizard} busy={busy} onLoadSheet={loadSheet} onAnalyze={analyze} onCommit={commit}
          setMapping={(m) => setWizard((w) => ({ ...w, mapping: m }))} onClose={() => setWizard(null)} />
      )}
      {reviewExc && (
        <ExceptionReview eventId={eventId} exc={reviewExc} onClose={() => setReviewExc(null)} onDone={() => { setReviewExc(null); refresh(); }} />
      )}
    </div>
  );
}

function ExceptionReview({ eventId, exc, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (status) => {
    if ((status === "resolved" || status === "ignored") && !note.trim()) { toast.error("Add a resolution note"); return; }
    setBusy(true);
    try { await api.dataResolveException(eventId, exc.id, { status, note }); toast.success(status === "resolved" ? "Exception resolved" : status === "ignored" ? "Exception acknowledged" : "Marked in review"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[200] flex justify-end bg-black/40" onClick={onClose} data-testid="exception-review-drawer">
      <div className="w-full max-w-md h-full bg-white p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-serif text-xl text-[hsl(var(--primary))]">Review Exception</h3><button onClick={onClose}><X className="h-5 w-5 text-[hsl(var(--muted-foreground))]" /></button></div>
        <div className="space-y-3 text-sm">
          <Field l="Attendee" v={exc.attendee_name || "Unmatched record"} />
          {exc.attendee_email && <Field l="Email" v={exc.attendee_email} />}
          <Field l="Source module" v={exc.source} />
          <Field l="Type" v={exc.type || exc.kind || "—"} />
          <Field l="Issue" v={exc.description} />
          <Field l="Created" v={exc.created_at ? new Date(exc.created_at).toLocaleString() : "—"} />
          <Field l="Status" v={exc.status} />
          <div>
            <label className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Resolution note</label>
            <textarea data-testid="exception-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] p-2 text-sm" placeholder="Describe how this was resolved…" />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => act("in_review")} disabled={busy} className="rounded-xl border border-[hsl(var(--border))] px-3 py-2 text-sm font-semibold text-[hsl(var(--primary))]">Mark in review</button>
          <button data-testid="exception-acknowledge" onClick={() => act("ignored")} disabled={busy} className="rounded-xl border border-[hsl(var(--border))] px-3 py-2 text-sm font-semibold text-[hsl(var(--primary))]">Acknowledge</button>
          <button data-testid="exception-confirm-resolve" onClick={() => act("resolved")} disabled={busy} className="rounded-xl bg-[hsl(var(--secondary))] text-white px-4 py-2 text-sm font-semibold disabled:opacity-60">{busy ? "Saving…" : "Confirm & Resolve"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ l, v }) {
  return <div className="flex justify-between gap-3 border-b border-[hsl(var(--border)/0.5)] pb-1.5"><span className="text-[hsl(var(--muted-foreground))] shrink-0">{l}</span><span className="text-[hsl(var(--primary))] font-medium text-right">{v}</span></div>;
}

function SumStat({ k, v }) {
  return (
    <div className="flex justify-between gap-2 border-b border-[hsl(var(--border)/0.6)] py-1 text-sm">
      <span className="text-[hsl(var(--muted-foreground))]">{k}</span>
      <span className="font-semibold text-[hsl(var(--primary))]">{v}</span>
    </div>
  );
}

function SumGroup({ title, children }) {
  return (
    <div className="premium-card p-4">
      <div className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--secondary))] mb-2">{title}</div>
      {children}
    </div>
  );
}

function PreCommitSummary({ summary, counts, busy, onBack, onCommit }) {
  const s = summary || {};
  const money = (m) => (m && m.display) || "$0.00";
  return (
    <div data-testid="wizard-precommit-summary">
      <h3 className="font-semibold text-[hsl(var(--primary))] mb-1">Pre-commit summary</h3>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Review these figures before committing. Nothing has been saved yet.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <SumGroup title="Import">
          <SumStat k="Valid attendee rows" v={s.valid_attendee_rows ?? 0} />
          <SumStat k="New" v={s.new ?? 0} />
          <SumStat k="Updated" v={s.updated ?? 0} />
          <SumStat k="Unchanged" v={s.unchanged ?? 0} />
          <SumStat k="Skipped (section/blank)" v={s.skipped_rows ?? 0} />
          <SumStat k="Review" v={s.review ?? 0} />
          <SumStat k="Errors" v={s.errors ?? 0} />
        </SumGroup>
        <SumGroup title="RSVP">
          <SumStat k="Attending" v={s.rsvp_attending ?? 0} />
          <SumStat k="Pending" v={s.rsvp_pending ?? 0} />
          <SumStat k="Declined" v={s.rsvp_declined ?? 0} />
        </SumGroup>
        <SumGroup title="Attendee Category">
          <SumStat k="PC Winner" v={s.pc_winner ?? 0} />
          <SumStat k="MDC Winner" v={s.mdc_winner ?? 0} />
          <SumStat k="Non-PC Attendee" v={s.non_pc ?? 0} />
          {s.sprint ? <SumStat k="Sprint-to-PC" v={s.sprint} /> : null}
        </SumGroup>
        <SumGroup title="Guest">
          <SumStat k="Plus-one: Yes" v={s.plus_one_yes ?? 0} />
          <SumStat k="Plus-one: No" v={s.plus_one_no ?? 0} />
          <SumStat k="Plus-one: Unknown" v={s.plus_one_unknown ?? 0} />
        </SumGroup>
        <SumGroup title="Accommodation">
          <SumStat k="Calculation available" v={s.accommodation_available ?? 0} />
          <SumStat k="Calculation pending" v={s.accommodation_pending ?? 0} />
          <SumStat k="Total covered nights" v={s.covered_nights_total ?? 0} />
          <SumStat k="Total personal extension nights" v={s.extension_nights_total ?? 0} />
        </SumGroup>
        <SumGroup title="Accommodation Exposure (system calculated)">
          <SumStat k="Company — confirmed (Attending)" v={money(s.company_accommodation_confirmed)} />
          <SumStat k="Employee — confirmed (Attending)" v={money(s.employee_accommodation_confirmed)} />
          <SumStat k="Company — pending RSVP estimate" v={money(s.company_accommodation_pending_estimate)} />
          <SumStat k="Employee — pending RSVP estimate" v={money(s.employee_accommodation_pending_estimate)} />
        </SumGroup>
      </div>
      <p className="mt-3 text-[11px] text-[hsl(var(--muted-foreground))]">Confirmed exposure counts Attending attendees only; Declined are excluded; Pending RSVP figures are planning estimates, not confirmed spend. Employee amounts are payable directly to the hotel.</p>
      <div className="mt-4 flex items-center gap-2">
        <button onClick={onBack} data-testid="summary-back" className="rounded-xl border border-[hsl(var(--border))] px-4 py-2 text-sm font-semibold text-[hsl(var(--primary))]">Back to Review</button>
        <button onClick={onCommit} disabled={busy} data-testid="wizard-commit" className="inline-flex items-center gap-2 rounded-xl bg-[hsl(var(--secondary))] text-white px-4 py-2 text-sm font-semibold disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Commit approved changes <CheckCircle2 className="h-4 w-4" /></>}</button>
      </div>
    </div>
  );
}

function Wizard({ wizard, busy, onLoadSheet, onAnalyze, onCommit, setMapping, onClose, setWizard }) {
  const { step, job, sheetInfo, mapping, analysis } = wizard;
  const [expanded, setExpanded] = useState({});
  const toggle = (row) => setExpanded((e) => ({ ...e, [row]: !e[row] }));
  const steps = ["sheet", "map", "review", "summary"];
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="import-wizard">
      <div className="absolute inset-0 bg-[hsl(var(--primary)/0.4)] backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-[88vh] overflow-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[hsl(var(--border))] px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            {steps.map((s, i) => (<React.Fragment key={s}><span className={`capitalize font-semibold ${step === s ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))]"}`}>{s}</span>{i < steps.length - 1 && <ChevronRight className="h-3 w-3 text-[hsl(var(--muted-foreground))]" />}</React.Fragment>))}
          </div>
          <button onClick={onClose} data-testid="wizard-close"><X className="h-5 w-5 text-[hsl(var(--muted-foreground))]" /></button>
        </div>
        <div className="p-5">
          {step === "sheet" && (
            <div>
              <h3 className="font-semibold text-[hsl(var(--primary))] mb-3">Select worksheet — {job.file_name}</h3>
              <div className="space-y-2">
                {job.sheets.map((s) => (
                  <button key={s.name} data-testid="sheet-option" onClick={() => onLoadSheet(s.name)} disabled={busy} className="flex w-full items-center justify-between rounded-lg border border-[hsl(var(--border))] p-3 hover:border-[hsl(var(--secondary))] text-left">
                    <span className="font-medium text-[hsl(var(--primary))]">{s.name}</span>
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{s.row_count} rows · {s.column_count} cols <ArrowRight className="inline h-3 w-3" /></span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === "map" && sheetInfo && (
            <div>
              <h3 className="font-semibold text-[hsl(var(--primary))] mb-1">Map columns {sheetInfo.applied_template ? `(template: ${sheetInfo.applied_template})` : ""}</h3>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{sheetInfo.row_count} rows. Match your file's columns to canonical fields.</p>
              <div className="grid sm:grid-cols-2 gap-2 max-h-[45vh] overflow-auto pr-1">
                {sheetInfo.headers.map((h) => (
                  <div key={h} className="flex items-center gap-2 text-sm">
                    <span className="w-1/2 truncate text-[hsl(var(--muted-foreground))]" title={h}>{h}</span>
                    <select data-testid="map-select" value={mapping[h] || ""} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value || null })} className="w-1/2 rounded-lg border border-[hsl(var(--border))] px-2 py-1.5 text-sm">
                      <option value="">— ignore —</option>
                      {sheetInfo.canonical_fields.map((f) => <option key={f.key} value={f.key}>{f.label}{f.required ? " *" : ""}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <button onClick={onAnalyze} disabled={busy} data-testid="wizard-analyze" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Validate & Match <ArrowRight className="h-4 w-4" /></>}</button>
            </div>
          )}
          {step === "review" && analysis && (
            <div>
              <h3 className="font-semibold text-[hsl(var(--primary))] mb-3">Review changes</h3>
              <div className="flex flex-wrap gap-2 mb-3 text-xs">
                {Object.entries(analysis.counts).filter(([k]) => k !== "total").map(([k, v]) => <span key={k} className={`rounded-full px-2.5 py-1 font-semibold capitalize ${BADGE[k] || "bg-[hsl(var(--muted))]"}`} data-testid={`count-${k}`}>{k}: {v}</span>)}
              </div>
              <div className="max-h-[42vh] overflow-auto border border-[hsl(var(--border))] rounded-lg">
                <table className="w-full text-sm"><thead className="bg-[hsl(var(--muted))] sticky top-0"><tr><th className="text-left p-2">Name</th><th className="text-left p-2">Status</th><th className="text-left p-2">Details</th></tr></thead>
                  <tbody>
                    {analysis.results.map((r) => {
                      const isTM = (wizard.sourceType || "TRAVEL_MASTER") === "TRAVEL_MASTER";
                      const canExpand = isTM && ["new", "updated", "review", "unchanged", "cancelled"].includes(r.status) && (r.resulting || r.canonical);
                      const isOpen = !!expanded[r.row];
                      return (
                      <React.Fragment key={r.row}>
                      <tr className="border-t border-[hsl(var(--border))]" data-testid="review-row">
                        <td className="p-2 align-top">
                          {canExpand ? (
                            <button onClick={() => toggle(r.row)} data-testid="expand-row" className="flex items-start gap-1.5 text-left group">
                              {isOpen ? <ChevronDown className="h-4 w-4 mt-0.5 text-[hsl(var(--secondary))] shrink-0" /> : <ChevronRight className="h-4 w-4 mt-0.5 text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--secondary))] shrink-0" />}
                              <span><span className="font-medium text-[hsl(var(--primary))] group-hover:underline">{r.name || "—"}</span><span className="block text-xs text-[hsl(var(--muted-foreground))]">{r.email}</span></span>
                            </button>
                          ) : (
                            <div className="pl-5"><div className="font-medium text-[hsl(var(--primary))]">{r.name || "—"}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{r.email}</div></div>
                          )}
                        </td>
                        <td className="p-2 align-top"><span className={`text-[10px] font-bold rounded-full px-2 py-0.5 capitalize ${BADGE[r.status]}`}>{r.status === "skipped" ? "skipped · blank" : r.status}</span></td>
                        <td className="p-2 text-xs text-[hsl(var(--muted-foreground))] align-top">
                          {r.errors?.length > 0 && <div className="text-[hsl(0_60%_45%)]">{r.errors.join(", ")}</div>}
                          {r.status !== "skipped" && r.warnings?.length > 0 && r.warnings.map((w, i) => <div key={i} className="text-[hsl(38_50%_35%)]" data-testid="row-warning">{w}</div>)}
                          {r.status === "skipped" && <div className="italic">Blank row — ignored, will not be committed</div>}
                          {r.match_name && <div>Matched: <span className="font-medium text-[hsl(var(--primary))]">{r.match_name}</span> <span className="text-[hsl(var(--muted-foreground))]">({r.confidence}%)</span></div>}
                          {r.status === "unmatched" && <div className="text-[hsl(0_60%_45%)]">No attendee match ({r.confidence}%) — creates an unmatched exception</div>}
                          {r.status === "updated" && Object.entries(r.changes).slice(0, 3).map(([f, c]) => <div key={f}>{f}: <span className="line-through">{String(c.previous ?? "—")}</span> → <span className="text-[hsl(var(--primary))]">{String(c.new)}</span></div>)}
                          {r.status === "updated" && Object.keys(r.changes).length > 3 && <div className="text-[hsl(var(--muted-foreground))]">+{Object.keys(r.changes).length - 3} more — expand to view</div>}
                          {r.status === "new" && <div className="text-[hsl(var(--primary))]">New attendee — expand to preview record</div>}
                          {r.impacts?.map((im, i) => <div key={i} className={im.severity === "high" ? "text-[hsl(0_60%_45%)]" : "text-[hsl(38_50%_35%)]"} data-testid="impact-item">{im.description}</div>)}
                          {r.manual_conflicts?.length > 0 && <div className="text-[hsl(38_50%_35%)]">Manual conflict: {r.manual_conflicts.map((m) => m.field).join(", ")}</div>}
                        </td>
                      </tr>
                      {isOpen && canExpand && (
                        <tr className="border-t border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.25)]" data-testid="review-row-expanded">
                          <td colSpan={3} className="p-3">
                            <div className="text-[10px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">{r.status === "new" ? "Record that will be created" : "Resulting record after import"}</div>
                            <CanonicalPreview rec={r.resulting || r.canonical} segments={r.flight_segments} accom={r.accommodation} />
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]" data-testid="commit-safety-note">Only valid New and Updated records are committed. Errors and blank (skipped) rows are never committed. Review the Summary before committing.</p>
              <button onClick={() => setWizard((w) => ({ ...w, step: "summary" }))} data-testid="wizard-to-summary" className="mt-2 inline-flex items-center gap-2 rounded-xl bg-[hsl(var(--secondary))] text-white px-4 py-2 text-sm font-semibold">Continue to Summary <ArrowRight className="h-4 w-4" /></button>
            </div>
          )}
          {step === "summary" && analysis && (
            <PreCommitSummary summary={analysis.summary} counts={analysis.counts}
              busy={busy} onBack={() => setWizard((w) => ({ ...w, step: "review" }))} onCommit={onCommit} />
          )}
          {step === "done" && (
            <div className="text-center py-6" data-testid="wizard-summary">
              <CheckCircle2 className="h-12 w-12 text-[hsl(var(--mint))] mx-auto mb-3" />
              <h3 className="font-serif text-2xl text-[hsl(var(--primary))]">Import complete</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{(wizard.sourceType === "HYATT" || wizard.sourceType === "AMSTAR")
                ? <>{wizard.commit?.matched} matched · {wizard.commit?.review} review · {wizard.commit?.unmatched} unmatched · {wizard.commit?.cancelled} cancelled · {wizard.commit?.exceptions} exceptions</>
                : <>{wizard.commit?.created} new · {wizard.commit?.updated} updated · {wizard.commit?.cancelled} cancelled · {wizard.commit?.skipped} skipped · {wizard.commit?.exceptions} exceptions</>}</p>
              <button onClick={onClose} className="mt-5 rounded-xl bg-[hsl(var(--primary))] text-white px-5 py-2 text-sm font-semibold">Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
